<template>
  <div class="automation-schedule">
    <AppNoResults
      v-if="!actions.length"
      design="new"
      title="No saved actions"
      text="This run has no materialized schedule."
    />
    <AppDataTable v-else design="new" density="dense" :columns="columns" :rows="actions" min-width="1200px" clickable :is-row-expanded="isExpanded" @row-click="toggleAction($event.row.id)">
      <template #cell-name="{ row: action }"><div class="ui-actions ui-actions--tight"><AppIconButton :label="expandedActionId === action.id ? 'Collapse follow-up' : 'Expand follow-up'" @click="toggleAction(action.id)"><i :class="expandedActionId === action.id ? 'pi pi-chevron-down' : 'pi pi-chevron-right'" aria-hidden="true" /></AppIconButton><AppTableText :text="`${action.position}. ${action.name}`" /></div></template>
      <template #cell-type="{ row }"><AppTableText :text="display(row.action_type)" /></template>
      <template #cell-due="{ row }"><AppTableText :text="dateTime(row.due_at)" /></template>
      <template #cell-started="{ row }"><AppTableText :text="dateTime(row.started_at)" /></template>
      <template #cell-result="{ row }"><AppTableText :text="actionResult(row)" /></template>
      <template #cell-deliveries="{ row }"><AppTableText :text="deliverySummary(row.deliveries)" /></template>
      <template #cell-status="{ row }"><div class="ui-cell-inline"><AppStatusBadge v-if="row.is_manual" design="new" compact tone="blue" label="Manual" /><AppStatusBadge design="new" compact :tone="statusTone(row.status)" :label="display(row.status)" /></div></template>
      <template #row-detail="{ row: action }">
                <div class="automation-schedule-detail-grid">
                  <DetailCard title="Overview">
                    <Fields :fields="overviewFields(action)" />
                    <p v-if="action.error && !action.attention" class="automation-problem automation-schedule-note">
                      <strong>{{ action.error.code }}</strong> · {{ action.error.message }}
                    </p>
                    <div v-if="action.attention" class="automation-attention-note">
                      <strong>{{ attentionLabel(action.attention) }} · {{ action.attention.title }}</strong>
                      <span>{{ action.attention.message }}</span>
                      <small>{{ action.attention.recommended_action }}</small>
                    </div>
                    <AppButton
                      v-if="action.communication"
                      design="new"
                      theme="dark"
                      size="small"
                      class="automation-schedule-action"
                      @click.native.stop="$emit('open-communication', action.communication.id)"
                    >Communication details</AppButton>
                  </DetailCard>

                  <DetailCard
                    v-for="delivery in action.deliveries"
                    :key="delivery.id"
                    :title="display(delivery.provider)"
                  >
                    <template #actions>
                      <AppStatusBadge
                        v-if="delivery.has_attention"
                        design="new"
                        compact
                        :tone="delivery.attention.level === 'send_failed' ? 'red' : 'amber'"
                        :label="attentionLabel(delivery.attention)"
                      />
                      <AppStatusBadge
                        design="new"
                        compact
                        :tone="statusTone(delivery.status)"
                        :label="display(delivery.status)"
                      />
                    </template>
                    <Fields :fields="deliveryFields(delivery)" />
                    <p v-if="delivery.error" class="automation-problem automation-schedule-note">
                      {{ delivery.error.message }}
                    </p>
                    <p v-if="delivery.attention" class="automation-muted automation-schedule-note">
                      {{ delivery.attention.recommended_action }}
                    </p>
                    <AppButton
                      design="new"
                      theme="dark"
                      size="small"
                      class="automation-schedule-action"
                      @click.native.stop="$emit('open-delivery', delivery.id, $event.currentTarget)"
                    >Delivery details</AppButton>
                  </DetailCard>
                </div>
      </template>
    </AppDataTable>
  </div>
</template>

<script>
import { display, dateTime, statusTone } from './model'
import DetailCard from './DetailCard.vue'
import Fields from './Fields.vue'
import AppTableText from '@/components/AppTableText.vue'

export default {
  name: 'FollowUpAutomationScheduleActions',
  components: { DetailCard, Fields, AppTableText },
  props: { actions: { type: Array, required: true } },
  data: () => ({ expandedActionId: null,
    columns: [
      { key: 'name', label: 'Follow-up', width: '220px' }, { key: 'type', label: 'Type', width: '140px' },
      { key: 'due', label: 'Due', width: '160px' }, { key: 'started', label: 'Started', width: '160px' },
      { key: 'result', label: 'Result', width: '180px' }, { key: 'deliveries', label: 'Deliveries', width: '160px' }, { key: 'status', label: 'Status', width: '170px' }
    ] }),
  methods: {
    display,
    dateTime,
    statusTone,
    attentionLabel(attention) {
      return attention.level === 'send_failed' ? 'Send failed' : 'Not sent'
    },
    isExpanded(action) { return this.expandedActionId === action.id },
    toggleAction(id) {
      this.expandedActionId = this.expandedActionId === id ? null : id
    },
    actionResult(action) {
      if (action.sent_at) return `Sent · ${dateTime(action.sent_at)}`
      if (action.cancelled_at) return `Cancelled · ${dateTime(action.cancelled_at)}`
      return '—'
    },
    deliverySummary(deliveries) {
      if (!deliveries || !deliveries.length) return '—'
      return deliveries.map(delivery => display(delivery.provider)).join(' / ')
    },
    overviewFields(action) {
      return this.compactFields([
        ['Type', display(action.action_type)],
        ['Due', action.due_at && dateTime(action.due_at)],
        ['Started', action.started_at && dateTime(action.started_at)],
        ['Sent', action.sent_at && dateTime(action.sent_at)],
        ['Cancelled', action.cancelled_at && dateTime(action.cancelled_at)],
        ['Triggered by', action.triggered_by && display(action.triggered_by)],
        ['Flow update', action.flow_event_id && 'Recorded']
      ])
    },
    deliveryFields(delivery) {
      return this.compactFields([
        ['Channel', display(delivery.channel)],
        ['Scheduled', delivery.due_at && dateTime(delivery.due_at)],
        ['Account', delivery.account && display(delivery.account)],
        ['Provider status', delivery.provider_status && display(delivery.provider_status)],
        ['Communication', delivery.communication_status && display(delivery.communication_status)],
        ['Provider request', delivery.attention && (delivery.attention.provider_attempted ? 'Yes' : 'No')],
        ['Automatic retry', delivery.attention && (delivery.attention.automatic_retry_scheduled ? 'Scheduled' : 'Not scheduled')],
        ['Recorded attempts', this.attemptLabel(delivery.recorded_attempts)]
      ])
    },
    compactFields(fields) {
      return fields.filter(([, value]) => value !== null && value !== undefined && value !== '')
    },
    attemptLabel(attempts) {
      if (!attempts || attempts.kind === 'none') return ''
      if (attempts.kind === 'provider_record') return '1 provider record'
      if (attempts.kind === 'log') return 'Saved provider log'
      const count = Number(attempts.count) || 0
      return `${count} ${count === 1 ? 'attempt' : 'attempts'}`
    }
  }
}
</script>

<style scoped lang="scss">
.automation-schedule-table {
  min-width: 980px;
}
.automation-schedule-row > td:first-child {
  min-width: 220px;
  white-space: nowrap;
}
.automation-schedule-row__title {
  display: inline-grid;
  min-width: 0;
  gap: 2px;
  vertical-align: middle;
}
.automation-schedule-row__title small {
  color: $ui-muted;
  font-size: $ui-font-size-metadata;
}
.automation-schedule-row__badges {
  display: flex;
  align-items: center;
  gap: $ui-space-8;
}
.automation-schedule :deep(.app-status-badge--new) {
  color: #fff;
}
.automation-schedule-row:focus-visible {
  outline: 2px solid $ui-blue;
  outline-offset: -2px;
}
.automation-attempt-chevron {
  display: inline-flex;
  width: 18px;
  height: 18px;
  align-items: center;
  justify-content: center;
  margin-right: $ui-space-8;
  vertical-align: middle;
  transform: rotate(-90deg);
  transition: transform .2s ease;
}
.automation-attempt-chevron--open {
  transform: rotate(0);
}
.automation-attempt-chevron img {
  width: 10px;
  height: 6px;
}
.automation-schedule-detail-cell {
  padding: $ui-space-16 !important;
  background: $ui-surface-soft;
  white-space: normal !important;
}
.automation-schedule-detail-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-items: stretch;
  gap: $ui-space-12;
}
.automation-schedule-detail-grid > .automation-detail-card {
  height: 100%;
}
.automation-schedule-note {
  margin: $ui-space-12 0 0;
}
.automation-schedule-action {
  margin-top: $ui-space-16;
}
.automation-attention-note {
  display: grid;
  gap: $ui-space-4;
  margin-top: $ui-space-12;
  padding: $ui-space-12;
  border: 1px solid $ui-amber;
  border-radius: $ui-control-radius;
  background: $ui-amber-soft;
}
.automation-attention-note span,
.automation-attention-note small {
  display: block;
}
.automation-attention-note small {
  color: $ui-muted;
}
</style>
